'use client'

import classNames from 'classnames'
import React, { FunctionComponent, useState } from 'react'
import { FormControl } from '../FormControl/FormControl'
import styles from './Input.module.css'

type InputProps = {
  name: string
  ref?: React.Ref<HTMLInputElement>
  className?: string
  placeholderText?: string
  hasSubmitted?: boolean
  hasErrors?: boolean
  testId?: string
  value?: string
  label: string
  isSingleInput?: boolean
  required?: boolean
  variant?: 'inline' | 'outlined'
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * Consolidated text input used across the ticketing UI.
 *
 * It preserves two legacy behaviors behind a single API:
 * - `inline`: the compact floating-placeholder field used by `SingleInputForm`
 * - `outlined`: the checkout-style floating-label field that previously came from `Forms/Input2`
 *
 * The component stays intentionally narrow: it only handles shared text-input rendering,
 * focus state for label/placeholder transitions, and the controlled/uncontrolled wiring
 * needed by its existing call sites.
 */
const Input: FunctionComponent<InputProps> = ({
  label,
  name,
  ref: externalRef,
  className,
  placeholderText,
  testId = name,
  hasErrors = false,
  hasSubmitted = false,
  onChange,
  value = '',
  isSingleInput = false,
  required = false,
  variant = 'inline',
}: InputProps) => {
  const [hasValue, setHasValue] = useState(!!value)
  const [focus, setFocus] = useState(false)
  const errorsAndSubmitted = hasErrors && hasSubmitted
  const isOutlined = variant === 'outlined'
  const isHasValue = onChange ? !!value : hasValue

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setHasValue(!!e.target.value)
    onChange?.(e)
  }

  const containerClassName = classNames(className, {
    [styles.SingleInput]: isSingleInput,
    [styles.Outlined]: isOutlined,
  })
  const inputProps = {
    type: 'text' as const,
    ref: externalRef,
    'data-testid': `${testId}-input`,
    onFocus: () => setFocus(true),
    onBlur: () => setFocus(false),
    name,
    required,
    onChange: handleOnChange,
    ...(label
      ? {
        label,
        'aria-label': label,
      }
      : {}),
    ...(onChange
      ? { value: value || '' }
      : { defaultValue: value || '' }),
  }

  if (isOutlined) {
    return (
      <FormControl
        className={containerClassName}
        data-testid={`${testId}-container`}
        disabled={false}
        error={errorsAndSubmitted}
        required={required}
      >
        <div
          className={classNames(styles.OutlinedWrapper, { [styles.OutlinedFocused]: focus })}
        >
          <input
            className={classNames(styles.OutlinedInput, { [styles.InputHasErrors]: errorsAndSubmitted })}
            id={name}
            placeholder=" "
            {...inputProps}
          />
          <label className={styles.OutlinedLabel} htmlFor={name}>
            {label}
          </label>
        </div>
        <div className="helptext" data-testid="helptext">{'\u00A0'}</div>
      </FormControl>
    )
  }

  return (
    <div
      className={containerClassName}
      data-testid={`${testId}-container`}
    >
      <div
        className={classNames(
          styles.InputFieldWrapper,
          'input-field-wrapper',
        )}
      >
        {placeholderText && (
          <span
            className={classNames(
              styles.InputPlaceholderText,
              {
                [styles.HasErrors]: errorsAndSubmitted,
                focused: focus,
                [styles.HasValue]: isHasValue,
                'has-value': isHasValue,
              },
            )}
            data-testid={`${testId}-placeholder`}
          >
            {placeholderText}
          </span>
        )}
        <input
          className={classNames(
            'input-field',
            styles.InputField,
            { [styles.InputHasErrors]: errorsAndSubmitted },
          )}
          aria-placeholder={placeholderText}
          {...inputProps}
        />
      </div>
    </div>
  )
}

export default Input
